import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useEditorStore } from '../../store/editor.store'
import { BlockRenderer } from '../blocks/renderers'
import { getBlock, coreBlockHtml, isDesignSystemV2 } from '@bst/blocks'
import type { BlockState, BlockVisualStyles } from '../../store/editor.store'
import { useEffect, useMemo, useRef, useState } from 'react'
import { api, projectsApi } from '../../utils/api'
import { DESIGN_STYLE_MAP } from '../../data/designStyles'

export function buildVisualStylesCss(v?: BlockVisualStyles): React.CSSProperties {
  if (!v) return {}
  const css: React.CSSProperties = {}
  if (v.borderRadius) css.borderRadius = v.borderRadius
  if (v.borderWidth) {
    css.border = `${v.borderWidth} ${v.borderStyle ?? 'solid'} ${v.borderColor ?? 'currentColor'}`
  }
  if (v.boxShadow) css.boxShadow = v.boxShadow
  if (v.opacity !== undefined) css.opacity = v.opacity
  if (v.backdropFilter) css.backdropFilter = v.backdropFilter
  if (v.mixBlendMode) css.mixBlendMode = v.mixBlendMode as React.CSSProperties['mixBlendMode']
  if (v.overflow) css.overflow = v.overflow
  const transforms: string[] = []
  if (v.rotate) transforms.push(`rotate(${v.rotate})`)
  if (v.scale) transforms.push(`scale(${v.scale})`)
  if (transforms.length) css.transform = transforms.join(' ')
  return css
}

interface Props {
  block: BlockState
  sectionId: string
}

// Blocks where double-click triggers inline text editing
const INLINE_EDITABLE = new Set([
  'heading', 'paragraph', 'button', 'quote', 'cta',
  'banner', 'newsletter', 'hero-centered', 'hero-split', 'hero-minimal',
])

// Monolithic iframe blocks — pointer-events must be none on the inner wrapper
// so clicks propagate to the outer div and selectBlock() fires correctly.
const FULL_PAGE_BLOCK_TYPES = new Set([
  'swiss-page', 'glassmorphism-page',
  'swiss-landing-page', 'brutal-landing-page', 'serif-landing-page',
  'cinema-landing-page', 'maximal-landing-page', 'y2k-landing-page',
  'organic-landing-page', 'film-landing-page', 'bento-landing-page',
  'bento-portfolio-page', 'cinema-portfolio-page', 'film-portfolio-page',
  'minimal-portfolio-page', 'organic-portfolio-page',
  'swiss-portfolio-page', 'brutal-portfolio-page', 'immersive-portfolio-page',
  'neo-portfolio-page',
  'ai-studio-page',
])

interface PeerSelection { clientId: string; color: string; userName: string }

export function CanvasBlock({ block, sectionId }: Props) {
  const { selectedBlockId, editingBlockId, selectBlock, setEditingBlock, removeBlock, duplicateBlock, updateBlock } =
    useEditorStore()
  const selected = selectedBlockId === block.id
  // Design system v2 (AI sites): core blocks render with the SAME HTML as the
  // published site, so the canvas is WYSIWYG. Texts are edited in the properties
  // panel; other block types keep their React renderer.
  const themeConfig = useEditorStore(s => s.settings.themeConfig)
  const designV2 = useMemo(() => isDesignSystemV2(themeConfig), [themeConfig])
  const v2Html = useMemo(
    () => (designV2 ? coreBlockHtml({ id: block.id, type: block.type, data: block.data as Record<string, unknown> }) : null),
    [designV2, block.id, block.type, block.data],
  )
  const inlineEditable = INLINE_EDITABLE.has(block.type) && !v2Html
  const editing = editingBlockId === block.id && !v2Html

  const [peerSelections, setPeerSelections] = useState<PeerSelection[]>([])
  const [aiOpen, setAiOpen] = useState(false)
  const [aiLoading, setAiLoading] = useState(false)
  const [aiCustomInput, setAiCustomInput] = useState('')
  const [mobileActionOpen, setMobileActionOpen] = useState(false)
  const aiRef = useRef<HTMLDivElement>(null)
  const aiInputRef = useRef<HTMLTextAreaElement>(null)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const touchStartPos = useRef({ x: 0, y: 0 })

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (aiRef.current && !aiRef.current.contains(e.target as Node)) {
        setAiOpen(false)
      }
    }
    if (aiOpen) {
      document.addEventListener('mousedown', onClickOutside)
      setTimeout(() => aiInputRef.current?.focus(), 50)
    }
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [aiOpen])

  async function saveAsComponent() {
    const projectId = localStorage.getItem('bst-project-id')
    if (!projectId) {
      alert('Компоненты доступны только в проектах Project Engine')
      return
    }
    const name = prompt('Название компонента:', getBlock(block.type)?.label ?? block.type)
    if (!name?.trim()) return
    try {
      await projectsApi.createComponent(projectId, {
        name: name.trim(),
        templateData: { blockType: block.type, blockData: block.data },
      })
    } catch {
      alert('Не удалось сохранить компонент')
    }
  }

  async function aiAction(instruction: string) {
    setAiOpen(false)
    setAiCustomInput('')
    setAiLoading(true)
    try {
      const context = `Instruction: ${instruction}. Current block data: ${JSON.stringify(block.data)}`
      const result = await api.post<Record<string, unknown>>('/ai/generate-copy', { blockType: block.type, context })
      if (result && typeof result === 'object' && Object.keys(result).length > 0) {
        updateBlock(sectionId, block.id, { ...block.data, ...result })
      }
    } catch { /* silently fail */ } finally {
      setAiLoading(false)
    }
  }

  useEffect(() => {
    function onSelection(e: Event) {
      const detail = (e as CustomEvent<{ clientId: string; color: string; userName: string; blockId: string | null }>).detail
      setPeerSelections(prev => {
        const filtered = prev.filter(p => p.clientId !== detail.clientId)
        if (detail.blockId === block.id) {
          return [...filtered, { clientId: detail.clientId, color: detail.color, userName: detail.userName }]
        }
        return filtered
      })
    }
    window.addEventListener('bst:selection', onSelection)
    return () => window.removeEventListener('bst:selection', onSelection)
  }, [block.id])

  // Disable drag when editing so text selection works
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
    data: { type: 'block', blockId: block.id, sectionId },
    disabled: editing,
  })

  function handleTouchStart(e: React.TouchEvent) {
    touchStartPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
    longPressTimer.current = setTimeout(() => {
      selectBlock(block.id)
      setMobileActionOpen(true)
    }, 500)
  }

  function handleTouchMove(e: React.TouchEvent) {
    const dx = Math.abs(e.touches[0].clientX - touchStartPos.current.x)
    const dy = Math.abs(e.touches[0].clientY - touchStartPos.current.y)
    if (dx > 8 || dy > 8) {
      if (longPressTimer.current) clearTimeout(longPressTimer.current)
    }
  }

  function handleTouchEnd() {
    if (longPressTimer.current) clearTimeout(longPressTimer.current)
  }

  function handleDoubleClick(e: React.MouseEvent) {
    if (!inlineEditable) return
    e.stopPropagation()
    selectBlock(block.id)
    setEditingBlock(block.id)
  }

  function handleClick(e: React.MouseEvent) {
    e.stopPropagation()
    if (editing) return
    if (selected && inlineEditable) {
      setEditingBlock(block.id)
    } else {
      selectBlock(block.id)
    }
  }

  // Callback passed to renderer so it can save inline edits
  function handleUpdate(data: Record<string, unknown>) {
    updateBlock(sectionId, block.id, data)
    setEditingBlock(null)
  }

  const peerBorder = peerSelections.length > 0 ? peerSelections[0].color : null

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: 'relative',
    outline: selected
      ? editing
        ? `2px solid #22c55e`
        : `2px solid var(--accent)`
      : peerBorder
        ? `2px solid ${peerBorder}`
        : '2px solid transparent',
    outlineOffset: '-2px',
    cursor: 'default',
  }

  const hoverClass = [
    block.hoverEffect && !isDragging ? `canvas-block-hover effect-${block.hoverEffect}` : '',
    block.buttonEffect ? `bst-btn-effects bst-btn-effect-${block.buttonEffect}` : '',
  ].filter(Boolean).join(' ') || undefined

  const blockStyleDef = block.blockStyle ? DESIGN_STYLE_MAP[block.blockStyle] : null
  const blockStyleVars: Record<string, string> = blockStyleDef ? { ...blockStyleDef.cssVars } : {}

  // Typography vars — override design style vars
  const typo = block.blockTypography
  if (typo?.fontFamily) { blockStyleVars['--bst-font-body'] = typo.fontFamily; blockStyleVars['--bst-font-heading'] = typo.fontFamily }
  if (typo?.fontWeight) blockStyleVars['--bst-heading-weight'] = typo.fontWeight
  if (typo?.textAlign) blockStyleVars['--bst-text-align'] = typo.textAlign
  if (typo?.letterSpacing) blockStyleVars['--bst-letter-spacing'] = typo.letterSpacing
  if (typo?.lineHeight) blockStyleVars['--bst-line-height'] = typo.lineHeight
  if (typo?.headingSize) blockStyleVars['--bst-heading-size'] = typo.headingSize
  if (typo?.bodySize) blockStyleVars['--bst-body-size'] = typo.bodySize

  // Color vars
  const colors = block.blockColors
  if (colors?.bg) blockStyleVars['--bst-bg-override'] = colors.bg
  if (colors?.textColor) blockStyleVars['--bst-color-override'] = colors.textColor
  if (colors?.accentColor) blockStyleVars['--bst-accent-override'] = colors.accentColor

  // Button vars
  const btn = block.blockButton
  if (btn?.bg) blockStyleVars['--bst-btn-bg'] = btn.bg
  if (btn?.color) blockStyleVars['--bst-btn-color'] = btn.color
  if (btn?.radius !== undefined) blockStyleVars['--bst-r'] = `${btn.radius}px`
  if (btn?.borderColor) blockStyleVars['--bst-btn-border'] = btn.borderColor

  const hasBtnOverride = !!(btn?.bg || btn?.color)
  const styleRootClass = Object.keys(blockStyleVars).length > 0 ? 'bst-style-root' : undefined
  const btnSizeClass = btn?.paddingSize === 'sm' ? 'bst-btn-size-sm' : btn?.paddingSize === 'lg' ? 'bst-btn-size-lg' : undefined
  const innerClass = [styleRootClass, hasBtnOverride ? 'bst-btn-override' : null, btnSizeClass].filter(Boolean).join(' ') || undefined

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={hoverClass}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div
        className={innerClass}
        style={{
          ...(blockStyleVars as React.CSSProperties),
          ...(block.blockMinHeight ? { minHeight: `${block.blockMinHeight}px` } : {}),
          ...(block.wrapBackground ? { background: block.wrapBackground } : {}),
          ...(block.wrapPaddingTop !== undefined ? { paddingTop: `${block.wrapPaddingTop}px` } : {}),
          ...(block.wrapPaddingBottom !== undefined ? { paddingBottom: `${block.wrapPaddingBottom}px` } : {}),
          ...(block.wrapPaddingLeft !== undefined ? { paddingLeft: `${block.wrapPaddingLeft}px` } : {}),
          ...(block.wrapPaddingRight !== undefined ? { paddingRight: `${block.wrapPaddingRight}px` } : {}),
          ...(FULL_PAGE_BLOCK_TYPES.has(block.type) ? { pointerEvents: 'none' as const } : {}),
          ...buildVisualStylesCss(block.blockVisualStyles),
        }}
      >
        {v2Html !== null
          ? <div
              className="bst-v2-block"
              // Links inside the preview must not navigate away from the editor
              onClickCapture={(e) => { if ((e.target as HTMLElement).closest('a')) e.preventDefault() }}
              dangerouslySetInnerHTML={{ __html: v2Html }}
            />
          : <BlockRenderer
              block={block}
              selected={selected}
              editing={editing}
              onUpdate={handleUpdate}
            />}
      </div>

      {peerSelections.length > 0 && !selected && (
        <div style={{
          position: 'absolute', top: 0, left: 0,
          display: 'flex', gap: '2px', padding: '3px',
          zIndex: 5, pointerEvents: 'none',
        }}>
          {peerSelections.map(p => (
            <div
              key={p.clientId}
              style={{
                background: p.color,
                color: '#fff',
                fontSize: '10px',
                fontWeight: 600,
                padding: '1px 5px',
                borderRadius: '3px',
                lineHeight: '14px',
              }}
            >
              {p.userName}
            </div>
          ))}
        </div>
      )}

      {selected && !editing && (
        <div className="block-toolbar">
          <div
            className="block-drag-handle"
            {...listeners}
            {...attributes}
            title="Перетащить"
          >
            ⠿
          </div>
          <span className="block-type-label">{getBlock(block.type)?.label ?? block.type}</span>
          {inlineEditable && (
            <button
              className="block-action-btn"
              title="Редактировать текст (двойной клик)"
              onClick={(e) => { e.stopPropagation(); setEditingBlock(block.id) }}
            >
              ✏
            </button>
          )}
          {/* AI Magic button */}
          <div ref={aiRef} style={{ position: 'relative' }}>
            <button
              className="block-action-btn"
              title="AI улучшение"
              onClick={(e) => { e.stopPropagation(); setAiOpen(o => !o) }}
              style={{ background: aiLoading ? 'linear-gradient(135deg,#5b6af0,#8b5cf6)' : undefined, color: aiLoading ? '#fff' : undefined }}
            >
              {aiLoading ? '…' : '✨'}
            </button>
            {aiOpen && (
              <div onClick={e => e.stopPropagation()} style={{
                position: 'absolute', top: 'calc(100% + 4px)', right: 0, background: 'var(--bg-1)',
                border: '1px solid var(--border)', borderRadius: '12px', padding: '4px', zIndex: 200,
                boxShadow: '0 8px 24px rgba(0,0,0,0.18)', width: '210px',
              }}>
                {[
                  ['✨', 'Переписать текст', 'Completely rewrite all text content to be more engaging and professional'],
                  ['⬆', 'Улучшить качество', 'Improve the quality and clarity of all text, keep the same structure'],
                  ['✂', 'Сделать короче', 'Shorten all text content by 40-50%, keep the key points'],
                  ['📖', 'Развернуть', 'Expand and elaborate the text content with more details'],
                  ['💡', 'Добавить призыв', 'Add strong call-to-action elements and persuasive language'],
                ].map(([icon, label, instruction]) => (
                  <button key={label} onClick={() => aiAction(instruction as string)}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '7px 10px', background: 'none', border: 'none', borderRadius: '7px', fontSize: '12px', color: 'var(--text-primary)', cursor: 'pointer', textAlign: 'left' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-2)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                  >
                    <span style={{ fontSize: '13px' }}>{icon}</span>{label}
                  </button>
                ))}
                <div style={{ borderTop: '1px solid var(--border)', margin: '4px 6px 4px', opacity: 0.6 }} />
                <div style={{ padding: '4px 6px 6px', display: 'flex', gap: '6px', alignItems: 'flex-end' }}>
                  <textarea
                    ref={aiInputRef}
                    value={aiCustomInput}
                    onChange={e => setAiCustomInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        const v = aiCustomInput.trim()
                        if (v) aiAction(v)
                      }
                    }}
                    placeholder="Напишите что изменить..."
                    rows={1}
                    style={{
                      flex: 1, resize: 'none', border: '1px solid var(--border)',
                      borderRadius: '8px', padding: '6px 8px', fontSize: '12px',
                      background: 'var(--bg-2)', color: 'var(--text-primary)',
                      outline: 'none', fontFamily: 'inherit', lineHeight: 1.4,
                      maxHeight: '80px', overflowY: 'auto',
                    }}
                    onFocus={e => { e.currentTarget.rows = 2 }}
                    onBlur={e => { if (!aiCustomInput) e.currentTarget.rows = 1 }}
                  />
                  <button
                    onClick={() => { const v = aiCustomInput.trim(); if (v) aiAction(v) }}
                    disabled={!aiCustomInput.trim()}
                    style={{
                      width: 28, height: 28, border: 'none', borderRadius: '7px', flexShrink: 0,
                      background: aiCustomInput.trim() ? 'linear-gradient(135deg,#5b6af0,#7c3aed)' : 'var(--bg-3)',
                      color: aiCustomInput.trim() ? '#fff' : 'var(--text-muted)',
                      cursor: aiCustomInput.trim() ? 'pointer' : 'not-allowed',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'background 0.15s',
                    }}
                    title="Отправить (Enter)"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
          <button
            className="block-action-btn"
            title="Дублировать (Ctrl+D)"
            onClick={(e) => { e.stopPropagation(); duplicateBlock(sectionId, block.id) }}
          >
            ⧉
          </button>
          {localStorage.getItem('bst-project-id') && (
            <button
              className="block-action-btn"
              title="Сохранить как компонент"
              onClick={(e) => { e.stopPropagation(); void saveAsComponent() }}
            >
              ★
            </button>
          )}
          <button
            className="block-delete-btn"
            title="Удалить (Del)"
            onClick={(e) => { e.stopPropagation(); removeBlock(sectionId, block.id) }}
          >
            ✕
          </button>
        </div>
      )}

      {block.blockLinkUrl && !editing && (
        <div style={{
          position: 'absolute', top: 0, left: 0,
          background: 'rgba(91,106,240,0.88)', color: '#fff',
          fontSize: '10px', padding: '3px 8px',
          borderRadius: '0 0 6px 0', fontWeight: 600,
          zIndex: 9, pointerEvents: 'none', maxWidth: '200px',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          🔗 {block.blockLinkUrl}
        </div>
      )}

      {editing && (
        <div style={{
          position: 'absolute', top: 0, right: 0,
          background: '#22c55e', color: '#fff',
          fontSize: '10px', padding: '3px 8px',
          borderRadius: '0 0 0 6px', fontWeight: 600,
          zIndex: 10, pointerEvents: 'none',
        }}>
          ✏ Редактирование · Esc для выхода
        </div>
      )}

      {mobileActionOpen && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9900 }}
            onTouchStart={(e) => { e.stopPropagation(); setMobileActionOpen(false) }}
          />
          <div className="mobile-block-action-sheet" style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 9910 }}>
            <div style={{ width: '36px', height: '4px', background: 'var(--border)', borderRadius: '2px', margin: '10px auto 12px' }} />
            <div style={{ padding: '0 16px 8px', fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', marginBottom: '4px' }}>
              {getBlock(block.type)?.label ?? block.type}
            </div>
            {[
              { icon: '⧉', label: 'Дублировать',   action: () => { duplicateBlock(sectionId, block.id); setMobileActionOpen(false) }, danger: false },
              { icon: '✕', label: 'Удалить блок',   action: () => { removeBlock(sectionId, block.id); setMobileActionOpen(false) }, danger: true  },
            ].map(item => (
              <button
                key={item.label}
                className={`mobile-block-action-btn${item.danger ? ' danger' : ''}`}
                onTouchStart={(e) => { e.stopPropagation(); item.action() }}
              >
                <span style={{ fontSize: '16px', width: '24px', textAlign: 'center' }}>{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
            <div style={{ height: 'env(safe-area-inset-bottom, 8px)' }} />
          </div>
        </>
      )}
    </div>
  )
}
